import { ActionBar } from "$components/action-bar/action-bar";
import type { ActionItem } from "$components/action-bar/action-item";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import AddItemsModal from "$routes/price-list/_lib/components/modals/add-items-modal";
import ConnectToOrgPriceListModal from "$routes/price-list/_lib/components/modals/connect-to-org-price-list-modal";
import DisconnectFromOrgPriceListModal from "$routes/price-list/_lib/components/modals/disconnect-from-org-price-list-modal";
import { linkToBulkImport } from "$routes/price-list/links";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import { ItemPriceOwner } from "elephant-web/gql";
import { type Ref, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

type Props = {
  priceListId: string;
  priceListOwner: ItemPriceOwner;
  isConnectedToOrg: boolean;
  refetchPriceList: () => void;
  className?: string;
  containerRef?: Ref<HTMLElement>;
};

export const PriceListDefaultModeBar: FC<Props> = ({
  priceListId,
  priceListOwner,
  isConnectedToOrg,
  refetchPriceList,
  className,
  containerRef,
}) => {
  const navigate = useNavigate();
  const [bulkUploadFlag] = useFeatureFlag(
    featureFlags.billing.priceListBulkUpload,
  );

  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const showFacilityOrgActions = priceListOwner === ItemPriceOwner.Facility;
  const orgMenuItem: ActionItem | null = !showFacilityOrgActions
    ? null
    : isConnectedToOrg
      ? {
          id: "priceList.disconnectFromOrgPriceList.button",
          defaultMessage: "Disconnect from Org Price List",
          icon: <LinkOffIcon />,
          onClick: () => setShowDisconnectModal(true),
        }
      : {
          id: "priceList.connectToPriceList.button",
          defaultMessage: "Connect to Org Price List",
          icon: <LinkIcon />,
          onClick: () => setShowConnectModal(true),
        };

  const addItemsAction: ActionItem = {
    id: "priceList.addItems.button",
    defaultMessage: "Add Items",
    variant: "primary",
    icon: <AddIcon />,
    testId: "priceList.addItems.button",
    onClick: () => setShowAddItemsModal(true),
  };

  const bulkImportAction: ActionItem = {
    id: "priceList.bulkImport.button",
    defaultMessage: "Bulk Import",
    variant: "secondary",
    icon: <CloudUploadIcon />,
    testId: "priceList.bulkImport.button",
    onClick: () => navigate(linkToBulkImport(priceListId)),
  };

  const actions: ActionItem[] = [
    addItemsAction,
    ...(bulkUploadFlag ? [bulkImportAction] : []),
  ];

  return (
    <>
      <ActionBar
        className={className}
        containerRef={containerRef}
        actions={actions}
        moreActionsItems={orgMenuItem ? [orgMenuItem] : []}
        moreActionsTestId="priceList.moreActions.button"
        moreActionsLabel={
          <FormattedMessage
            id="priceList.moreActions.button"
            defaultMessage="More actions"
          />
        }
      />
      <AddItemsModal
        open={showAddItemsModal}
        onClose={() => setShowAddItemsModal(false)}
        priceListId={priceListId}
      />
      <ConnectToOrgPriceListModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onCompleted={refetchPriceList}
        priceListId={priceListId}
      />
      <DisconnectFromOrgPriceListModal
        priceListId={priceListId}
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onCompleted={refetchPriceList}
      />
    </>
  );
};
